/**
 * Selection Service
 *
 * Manages document selection and cursor synchronization between
 * the Word document and the taskpane paragraph list.
 */

/* global Office, Word */

import { CHAR_STYLE_CC_TAG } from "src/services";

/**
 * Selects a specific paragraph in the Word document UI, scrolling it into view.
 * Shows a blue selection box around the entire paragraph.
 *
 * @param uniqueLocalId - The paragraph's session-scoped unique ID
 */
export async function selectParagraphInDocument(uniqueLocalId: string): Promise<void> {
  try {
    await Word.run(async (context) => {
      const target = context.document.getParagraphByUniqueLocalId(uniqueLocalId);
      // `content`, not `whole`: `whole` includes the paragraph mark, and a
      // selection holding it reads as touching the next paragraph too — so the
      // selection-changed handler records two paragraphs and the UI shows a
      // multi-selection that doesn't match what was navigated to.
      const range = target.getRange(Word.RangeLocation.content);
      range.select(Word.SelectionMode.select);
      holdSelection(context);
      await context.sync();
    });
  } catch (err) {
    console.error(`[selectParagraphInDocument] ERROR - uniqueLocalId=${uniqueLocalId}`, err);
    console.error(`[selectParagraphInDocument] Error details:`, {
      name: (err as Error).name,
      message: (err as Error).message,
    });
    throw err;
  }
}

/**
 * Selects a specific paragraph in the Word document UI, scrolling it into view.
 * Shows a blue selection box around the entire paragraph.
 *
 * @param uniqueLocalId - The paragraph's session-scoped unique ID
 */
export async function selectTextInDocument(title: string): Promise<void> {
  try {
    await Word.run(async (context) => {
      const cc = context.document.contentControls.getByTitle(title);
      cc.load("items");
      await context.sync();
      if (cc.items.length === 0) {
        // Records styled before content-control anchoring have no matching
        // control in the document — skip selection instead of crashing.
        console.warn(`[selectTextInDocument] No content control found for title=${title}`);
        return;
      }
      // Not `cc.items[0].select()`: that selects the control's `whole` range,
      // which for a paragraph-level control also takes in the paragraph mark
      // after it. The mark makes the selection read as touching the next
      // paragraph, so the selection-changed handler resolves a different
      // record than the one being navigated to. `content` is just the text
      // between the control's tags.
      cc.items[0].getRange(Word.RangeLocation.content).select(Word.SelectionMode.select);
      holdSelection(context);
      await context.sync();
    });
  } catch (err) {
    console.error(`[selectTextInDocument] ERROR - title=${title}`, err);
    console.error(`[selectTextInDocument] Error details:`, {
      name: (err as Error).name,
      message: (err as Error).message,
    });
    throw err;
  }
}

/**
 * Clears the current selection by collapsing it to the start.
 * This removes the blue selection box without changing cursor position.
 */
export async function clearSelection(): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.select(Word.SelectionMode.start);
    await context.sync();
  });
}

/**
 * Moves the cursor to the very start of the document body.
 *
 * Used before a fresh styling run so the selection-changed handler and the
 * Review page's mount-sync effect don't just re-read the previous cursor
 * position and restore stale selection state.
 */
export async function selectDocumentStart(): Promise<void> {
  await Word.run(async (context) => {
    const range = context.document.body.getRange(Word.RangeLocation.start);
    range.select(Word.SelectionMode.select);
    await context.sync();
  });
}

/**
 * Determines which paragraph in the given list corresponds to the user's
 * current cursor position in the document.
 *
 * Only loads the selection's parent paragraph ID and does an in-memory
 * lookup against the provided list — no heavy document scanning.
 *
 * @param paragraphs - The current list of paragraphs to search
 * @returns The list indices of the matching paragraphs, or null if the
 *          cursor is on a paragraph not in the list
 */
export async function getSelectedParagraphIndices(
  paragraphs: { uniqueLocalId: string }[]
): Promise<number[] | null> {
  if (paragraphs.length === 0) {
    return null;
  }

  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.paragraphs.load("items");
    await context.sync();

    const uniqueLocalIds = selection.paragraphs.items.map((p) => p.uniqueLocalId);

    const indices = paragraphs.reduce((acc: number[], p, i) => {
      const match = uniqueLocalIds.includes(p.uniqueLocalId);
      if (match) {
        return [...acc, i];
      }
      return acc;
    }, []);

    return indices.length > 0 ? indices : null;
  });
}

let heldSelection: Word.Range | null = null;

/**
 * Remembers the current selection so the selection-changed event raised by
 * the add-in's own document edit can be told apart from the user moving.
 * Call inside the `Word.run` that made the edit. Queue it in the same batch as
 * the edit where possible, so the hold exists before the resulting event is handled.
 */
export function holdSelection(context: Word.RequestContext): void {
  releaseHeldSelection();
  const range = context.document.getSelection();
  range.track();
  heldSelection = range;
}

function releaseHeldSelection(): void {
  const held = heldSelection;
  if (!held) return;
  heldSelection = null;
  void Word.run(held, async (context) => {
    held.untrack();
    await context.sync();
  }).catch(() => undefined);
}

/**
 * Whether the selection is still exactly where {@link holdSelection} left it.
 * The hold is released as soon as the selection has moved.
 */
export async function isSelectionHeld(): Promise<boolean> {
  const held = heldSelection;
  if (!held) return false;

  try {
    const unchanged = await Word.run(held, async (context) => {
      const relation = held.compareLocationWith(context.document.getSelection());
      await context.sync();
      return relation.value === Word.LocationRelation.equal;
    });
    if (unchanged) return true;
  } catch {
    // The held range no longer resolves; treat the selection as moved.
  }
  releaseHeldSelection();
  return false;
}

/**
 * The character-style record the selection stands for: the control a bare
 * cursor sits in, or the control whose text is selected exactly. A wider or
 * partial selection is new text to style, so it resolves to none.
 */
export async function findCharacterSelectionTarget(
  context: Word.RequestContext,
  range: Word.Range
): Promise<string | null> {
  const paragraph = range.paragraphs.getFirst();
  const controls = paragraph.contentControls.getByTag(CHAR_STYLE_CC_TAG);
  // Selecting a paragraph with its mark should match the same control as selecting just its text.
  const clamped = range.intersectWithOrNullObject(paragraph.getRange(Word.RangeLocation.content));
  range.load("isEmpty,start,end");
  clamped.load("isNullObject");
  controls.load("items/title");
  await context.sync();
  if (controls.items.length === 0) return null;

  if (range.isEmpty) {
    const controlRanges = controls.items.map((cc) => cc.getRange().load("start,end"));
    await context.sync();
    let title: string | null = null;
    controlRanges.forEach((r, i) => {
      if (range.start >= r.start && range.end <= r.end) title = controls.items[i].title;
    });
    return title;
  }

  const target = clamped.isNullObject ? range : clamped;
  const relations = controls.items.map((cc) =>
    cc.getRange(Word.RangeLocation.content).compareLocationWith(target)
  );
  await context.sync();
  const index = relations.findIndex((r) => r.value === Word.LocationRelation.equal);
  return index === -1 ? null : controls.items[index].title;
}

/** The live Word selection's character-style target and the paragraphs it spans. */
export async function resolveCharacterSelection(): Promise<{
  targetId: string | null;
  paragraphIds: string[];
}> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const paragraphs = selection.paragraphs;
    paragraphs.load("items/uniqueLocalId");
    const targetId = await findCharacterSelectionTarget(context, selection);
    return { targetId, paragraphIds: paragraphs.items.map((p) => p.uniqueLocalId) };
  });
}

/**
 * Registers a callback that fires when the user changes their selection
 * in the Word document (e.g. clicking a different paragraph).
 *
 * @param callback - Function to invoke on each selection change
 * @returns A cleanup function that removes the handler
 */
export function registerSelectionChangedHandler(callback: () => void): () => void {
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, callback);

  return () => {
    Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
      handler: callback,
    });
  };
}
